import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useSortBy,
} from "react-table";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import {
  faCheck,
  faCheckCircle,
  faCheckDouble,
  faChevronCircleLeft,
  faCode,
  faCopy,
  faEdit,
  faEllipsisV,
  faEye,
  faMoneyCheckAlt,
  faPencil,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Swal from "sweetalert2";
import Select from "react-select";

const ReceiptVoucherList = ({ permissions }) => {
  const [data, setData] = useState([]);
  const cameFromOptions = useMemo(() => {
    const unique = new Set((data || []).map((v) => v.cameFrom).filter(Boolean));

    return [...unique].map((v) => ({
      value: v,
      label: v,
    }));
  }, [data]);
  const departmentOptions = useMemo(() => {
    const deptSet = new Set();

    (data || []).forEach((row) => {
      row.items?.forEach((item) => {
        if (item.departmentName) {
          deptSet.add(item.departmentName);
        }
      });
    });

    return [...deptSet].map((d) => ({
      value: d,
      label: d,
    }));
  }, [data]);

  const [globalFilter, setGlobalFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const sewadarId = localStorage.getItem("sewadarId");
  const [searchParams] = useSearchParams();
  const [cameFromFilter, setCameFromFilter] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState([]);

  const [pageSize, setPageSize] = useState(() => {
    return parseInt(localStorage.getItem("voucherPageSize")) || 25;
  });
  const centerCode = localStorage.getItem("code");
  const navigate = useNavigate();

  const handleEdit = (voucher) => {
    if (voucher.voucherType == "SR") {
      navigate("../addReceiptVoucher", { state: { voucher } });
    } else {
      navigate("../addDepttReceiptVoucher", { state: { voucher } });
    }
  };

  const handleClone = (voucher) => {
    if (voucher.voucherType == "SR") {
      navigate("../addReceiptVoucher", { state: { voucher, isClone: true } });
    } else {
      navigate("../addDepttReceiptVoucher", {
        state: { voucher, isClone: true },
      });
    }
  };
  const fetchVouchers = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(
        `${SERVER_BASE}/getReceiptVouchers/${centerCode}`
      ); // Your GET route

      setData(res.data || []);
    } catch (err) {
      console.error("Error fetching vouchers:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    let result = [...data];

    // 🔹 Came From filter
    if (cameFromFilter.length) {
      const cameFromValues = cameFromFilter.map((o) => o.value);
      result = result.filter((row) => cameFromValues.includes(row.cameFrom));
    }

    // 🔹 Department filter (inside items)
    if (departmentFilter.length) {
      const deptValues = departmentFilter.map((o) => o.value);

      result = result.filter((row) =>
        row.items?.some((item) => deptValues.includes(item.departmentName))
      );
    }

    return result;
  }, [data, cameFromFilter, departmentFilter]);

  useEffect(() => {
    fetchVouchers();
  }, []);

  // Handle View action
  const handleDepartmentalView = (row) => {
    navigate("../../previewReceiptVoucher", {
      state: { data: row.original },
    });
  };
  // Handle View action
  const handleApproveInventoryView = (row) => {
    navigate("../../ApproveReceiptVoucher", {
      state: { data: row.original },
    });
  };
  const handleExternalView = (row) => {
    navigate("../../PreviewExternalReceiptVoucher", {
      state: { id: row.original.id },
    });
  };

  const handleDelete = async (voucher) => {
    const result = await Swal.fire({
      title: "Delete Receipt Voucher?",
      text: `Are you sure you want to delete Receipt Voucher ${voucher.gsrsNo}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      const response = await apiClient.delete(
        `${SERVER_BASE}/receipt-voucher/${voucher.id}`
      );

      // Update UI instantly
      setData((prev) => prev.filter((v) => v.id !== voucher.id));

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: response?.data?.message,
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Delete failed", err);

      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: err?.response?.data?.message || "Unable to delete voucher",
      });
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "S.No",
        width: 45,
        minWidth: 45,
        maxWidth: 45,
        Cell: ({ row }) => row.index + 1,
      },
      {
        Header: "Ledger Name",
        accessor: "ledgerName",
        width: 160,
        minWidth: 160,
        maxWidth: 160,
        Cell: ({ value }) => (
          <div
            style={{
              width: "310px",
            }}
            title={value}
          >
            {value}
          </div>
        ),
      },

      {
        Header: "Deptt.",
        accessor: (row) => {
          const departments = row.items
            ?.map((item) => item.departmentName)
            .filter(Boolean); // remove null/undefined

          const uniqueDepartments = [...new Set(departments)]; // remove duplicates
          return uniqueDepartments.join(", ") || "";
        },
        id: "departments",
      },
      {
        Header: "Count",
        accessor: (row) => row.items?.length || 0,
        id: "itemsCount",
      },
      { Header: "Document No", accessor: "documentNo" },
      {
        Header: "Doc Dt",
        accessor: "documentDate",
        width: 80,
        minWidth: 80,
        maxWidth: 80,

        Cell: ({ value }) =>
          `${value ? moment(value).format("DD-MMM-YY") : "-"}`,
      },

      // { Header: "Purpose", accessor: "purposeName" },
      // {
      //   Header: "Prepared By", accessor: "createdByName", Cell: ({ row }) => {
      //     return row.original.createdByName ? `${row.original.createdByName} - ${row.original.createdByBadge} `
      //       : "-";
      //   }
      // },
      {
        Header: "RV No",
        width: 80,
        minWidth: 80,
        maxWidth: 80,
        accessor: "rvNo",
        Cell: ({ row }) => {
          return (
            <div>
              {" "}
              {row.original.receiptStatus == "Final" ? row.original.rvNo : "-"}
            </div>
          );
        },
      },
      {
        Header: "RV Dt",
        accessor: "entryDate",
        width: 80,
        minWidth: 80,
        maxWidth: 80,
        Cell: ({ row }) => {
          return row.original.entryDate
            ? moment(row.original.entryDate).format("DD MMM YY")
            : row.original.date
            ? moment(row.original.date).format("DD MMM YY")
            : "-";
        },
      },
      { Header: "Status", accessor: "receiptStatus" },
      {
        Header: "Preview",
        accessor: "preview",
        width: 90,
        minWidth: 90,
        maxWidth: 90,
        Cell: ({ row }) => {
          const { receiptStatus, voucherType } = row.original;

          return receiptStatus == "Final" && permissions?.read ? (
            <button
              className="btn btn-outline-success btn-sm"
              onClick={() => handleExternalView(row)}
            >
              {voucherType == "SR" ? "Receipt" : "DIM"}
            </button>
          ) : (
            "-"
          );
        },
      },
      //       {
      //         Header: "Type",
      //         accessor: "voucherType",
      //         width: 90,
      //         minWidth: 90,
      //         maxWidth: 90,
      //         Cell: ({ row }) => {
      //           const { voucherType } = row.original;
      // const display= voucherType=='SR'?'Normal':"Deptt. Inward Memo"
      //           return display
      //         },
      //       },
      {
        Header: "#",
        accessor: "actions",
        width: 85,
        minWidth: 85,
        maxWidth: 85,
        Cell: ({ row }) => {
          const {
            receiptStatus,
            createdAt,
            createdByName,
            updatedAt,
            updatedByName,
            finalApprovedAt,
            confirmationLetterId,
            created,
          } = row.original;
          return (
            <div className="dropdown" style={{ width: "5px" }}>
              {/* {(receiptStatus == "Final" && permissions?.read) ? (
                  
                    <button 
                      className="btn btn-outline-success btn-sm me-1"
                      onClick={() => handleExternalView(row)}
                    >Receipt
                    </button>
                  
                ):null} */}

              <button
                className="btn btn-light btn-sm"
                type="button"
                id={`dropdownMenuButton-${row.index}`}
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <FontAwesomeIcon icon={faEllipsisV} />
              </button>
              <ul
                className="dropdown-menu dropdown-menu-end"
                aria-labelledby={`dropdownMenuButton-${row.index}`}
              >
                {receiptStatus != "Final" && permissions?.edit && (
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handleEdit(row.original)}
                    >
                      <FontAwesomeIcon icon={faEdit} /> Edit
                    </button>
                  </li>
                )}
                {permissions?.write && (
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handleClone(row.original)}
                    >
                      <FontAwesomeIcon icon={faCopy} /> Clone
                    </button>
                  </li>
                )}
                {/* {(receiptStatus == "Final" && permissions?.read) ? (
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handleExternalView(row)}
                    >
                      <FontAwesomeIcon icon={faEye} /> Receipt
                    </button>
                  </li>
                ):null} */}
                {/* Divider */}
                {(createdByName || updatedByName || finalApprovedAt) && (
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                )}
                {receiptStatus != "Final" &&
                  receiptStatus != "Verified" &&
                  created?.id != sewadarId && (
                    <li>
                      <button
                        className="dropdown-item"
                        onClick={() => handleApproveInventoryView(row)}
                      >
                        <FontAwesomeIcon icon={faEye} /> Approve Inventories
                      </button>
                    </li>
                  )}
                {(sewadarId == "23" ||
                  sewadarId == "26" ||
                  sewadarId == "29") && (
                  <li>
                    <button
                      className="dropdown-item text-danger"
                      onClick={() => handleDelete(row.original)}
                    >
                      <FontAwesomeIcon icon={faTrash} /> Delete
                    </button>
                  </li>
                )}

                {(receiptStatus == "Verified" || receiptStatus == "Final") && (
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handleDepartmentalView(row)}
                    >
                      <FontAwesomeIcon icon={faEye} />{" "}
                      {receiptStatus == "Final"
                        ? `Departmental Preview`
                        : `Make this Receipt Final`}
                    </button>
                  </li>
                )}

                {/* Created Info */}
                {createdByName && (
                  <li className="dropdown-item">
                    <FontAwesomeIcon icon={faPlus} /> {createdByName}
                    {createdAt &&
                      ` (${moment(createdAt).format("DD-MMM-YY hh:mm A")})`}
                  </li>
                )}

                {/* Updated Info */}
                {updatedByName && (
                  <li className="dropdown-item">
                    <FontAwesomeIcon icon={faPencil} /> {updatedByName}
                    {updatedAt &&
                      ` (${moment(updatedAt).format("DD-MMM-YY hh:mm A")})`}
                  </li>
                )}

                {/* Final Approved Info */}
                {finalApprovedAt && (
                  <li className="dropdown-item">
                    <FontAwesomeIcon icon={faCheckCircle} /> {createdByName}
                    {` (${moment(finalApprovedAt).format(
                      "DD-MMM-YY hh:mm A"
                    )})`}
                  </li>
                )}
              </ul>
            </div>
          );
        },
      },
      {
        Header: "CL",
        accessor: "CL",
        width: 40,
        minWidth: 40,
        maxWidth: 40,
        Cell: ({ row }) => {
          const { receiptStatus, confirmationLetterId } = row.original;

          return receiptStatus == "Final" &&
            permissions?.read &&
            confirmationLetterId ? (
            <Link
              to={`/confirmationLetterPreview?letterId=${confirmationLetterId}&type=external`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#0d6efd", textDecoration: "none" }}
            >
              <button className="btn btn-sm btn-outline-success"> CL</button>
            </Link>
          ) : (
            "-"
          );
        },
      },
    ],
    [permissions]
  );

  const globalTextFilter = (rows, _, filterValue) => {
  if (!filterValue) return rows;

  const search = filterValue.toLowerCase();

  return rows.filter((row) => {
    // Combine all cell values (including computed ones)
    const rowText = Object.values(row.original)
      .join(" ")
      .toLowerCase();

    // Also include departments from items
    const deptText =
      row.original.items
        ?.map((i) => i.departmentName)
        .join(" ")
        .toLowerCase() || "";

    return rowText.includes(search) || deptText.includes(search);
  });
};


  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    setGlobalFilter: setSearch,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize: updatePageSize,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data: filteredData,
      globalFilter: globalTextFilter, // ✅ IMPORTANT
      initialState: {
        pageIndex: parseInt(localStorage.getItem("voucherPageIndex")) || 0,
        pageSize: pageSize,
      },
    },

    useGlobalFilter,
    useSortBy, // ✅ Added this line
    usePagination
  );

  useEffect(() => {
    setSearch(globalFilter);
  }, [globalFilter, setSearch]);

  useEffect(() => {
    localStorage.setItem("voucherPageIndex", pageIndex);
    localStorage.setItem("voucherPageSize", pageSize);
  }, [pageIndex, pageSize]);

  return (
    <div className="formcontainer mt-5">
      <div className="d-flex justify-content-between">
        <h3 className="mb-3">Receipt Vouchers</h3>
        <h6 className="mb-3">Count: {filteredData?.length}</h6>
      </div>
      <div className="mb-3 gap-2 d-flex ">
        {permissions?.write && (
          <button
            className="btn btn-outline-success w-auto"
            onClick={() => {
              navigate("../addReceiptVoucher");
            }}
          >
            Add All Receipts
          </button>
        )}
        {permissions?.write && (
          <button
            className="btn btn-outline-success w-auto"
            onClick={() => {
              navigate("../addDepttReceiptVoucher");
            }}
          >
            Add Deptt. Inward Memo
          </button>
        )}
        {permissions?.read && (
          <>
            <input
              type="text"
              className="form-control"
              placeholder="Search..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              style={{ width: "30%" }}
            />
            {permissions?.read && (
              <Select
                isMulti
                options={cameFromOptions}
                value={cameFromFilter}
                onChange={setCameFromFilter}
                placeholder="Came From"
                className="me-2"
                styles={{
                  container: (base) => ({
                    ...base,
                    width: "auto",
                  }),
                }}
              />
            )}
            <Select
              isMulti
              options={departmentOptions}
              value={departmentFilter}
              onChange={setDepartmentFilter}
              placeholder="Department"
              className="me-2"
              styles={{
                container: (base) => ({
                  ...base,
                  width: "auto",
                }),
              }}
            />

            <select
              className="form-select"
              style={{ width: "auto" }}
              value={pageSize}
              onChange={(e) => {
                const size = Number(e.target.value);
                updatePageSize(size);
                setPageSize(size);
              }}
            >
              {[25, 50, 100, 150].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </select>
          </>
        )}
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          {permissions?.read && (
            <>
              <div className="d-flex ">
                <table
                  {...getTableProps()}
                  className="table table-bordered"
                  style={{ fontSize: "13px" }}
                >
                  <thead className="table-light">
                    {headerGroups.map((headerGroup) => (
                      <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((col) => (
                          <th
                            {...col.getHeaderProps(col.getSortByToggleProps())} // ✅ add sorting toggle props
                            style={{
                              cursor: "pointer",
                              width: col.width,
                              minWidth: col.minWidth,
                              maxWidth: col.maxWidth,
                            }}
                          >
                            {col.render("Header")}
                            {col.isSorted ? (
                              col.isSortedDesc ? (
                                <span> 🔽</span>
                              ) : (
                                <span> 🔼</span>
                              )
                            ) : (
                              ""
                            )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...getTableBodyProps()}>
                    {page.length === 0 ? (
                      <tr>
                        <td colSpan={columns.length} className="text-center">
                          No data found.
                        </td>
                      </tr>
                    ) : (
                      page.map((row) => {
                        prepareRow(row);
                        return (
                          <tr {...row.getRowProps()} className="p-0 m-0">
                            {row.cells.map((cell) => (
                              <td
                                style={{ alignContent: "center" }}
                                {...cell.getCellProps()}
                              >
                                {cell.render("Cell")}
                              </td>
                            ))}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <div className="d-flex justify-content-between">
                <button
                  className="btn btn-secondary"
                  onClick={previousPage}
                  disabled={!canPreviousPage}
                >
                  <FontAwesomeIcon icon={faChevronCircleLeft}></FontAwesomeIcon>{" "}
                  Previous
                </button>
                <span>
                  {" "}
                  Page {pageIndex + 1} of {pageOptions.length}
                </span>
                <button
                  className="btn btn-secondary"
                  onClick={nextPage}
                  disabled={!canNextPage}
                >
                  Next{" "}
                  <FontAwesomeIcon
                    icon={faChevronCircleLeft}
                    rotation={180}
                  ></FontAwesomeIcon>
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ReceiptVoucherList;
